import { useLayoutEffect, useRef, useState } from 'react'
import { capacityForPane, shouldAcceptFitResize } from './fitPageSize'

const DEFAULT_ROW_PX = 28

interface UseFitPageSizeOptions {
  /** Pane whose height drives capacity (list body above pagination). */
  containerRef: React.RefObject<HTMLElement | null>
  customerGrouped: boolean
}

function readPaneBox(root: HTMLElement): { w: number; h: number } {
  const scroll = root.querySelector('.table-pane__scroll') as HTMLElement | null
  return {
    w: Math.round(root.clientWidth),
    h: Math.round(scroll?.clientHeight || root.clientHeight),
  }
}

function readHeaderHeight(root: HTMLElement, fallback: number): number {
  const thead = root.querySelector('thead') as HTMLElement | null
  const h = thead?.getBoundingClientRect().height ?? 0
  return h > 0 ? h : fallback
}

/** Prefer a data row — group chrome rows are taller and skew capacity. */
function sampleDataRowHeight(root: HTMLElement): number | null {
  const row = root.querySelector(
    'tbody tr:not(.table__group-header):not(.table__group-footer)',
  ) as HTMLElement | null
  const h = row?.getBoundingClientRect().height ?? 0
  return h > 8 ? h : null
}

/**
 * Measure how many customers/rows fit in the list pane.
 * Capacity updates on significant pane resize or group-mode switch — not on
 * tiny content reflows (those previously oscillated pageSize and pagination).
 */
export function useFitPageSize({ containerRef, customerGrouped }: UseFitPageSizeOptions): number {
  const [pageSize, setPageSize] = useState(8)
  const rowPxRef = useRef(DEFAULT_ROW_PX)
  const rowPxFrozenRef = useRef(false)
  const lastBoxRef = useRef({ w: 0, h: 0 })
  const customerGroupedRef = useRef(customerGrouped)
  customerGroupedRef.current = customerGrouped

  useLayoutEffect(() => {
    const root = containerRef.current
    if (!root) return

    function applyCapacity(box: { w: number; h: number }, force: boolean) {
      if (box.h < 48) return
      const prev = lastBoxRef.current
      if (!force && !shouldAcceptFitResize({ prev, next: box })) return
      lastBoxRef.current = box

      if (!rowPxFrozenRef.current) {
        const sampled = sampleDataRowHeight(root!)
        if (sampled != null) {
          rowPxRef.current = sampled
          rowPxFrozenRef.current = true
        }
      }

      const next = capacityForPane({
        paneHeight: box.h,
        headerHeight: readHeaderHeight(root!, rowPxRef.current),
        rowHeight: rowPxRef.current,
        customerGrouped: customerGroupedRef.current,
      })
      setPageSize((current) => (current === next ? current : next))
    }

    function measure(force = false) {
      const el = containerRef.current
      if (!el) return
      applyCapacity(readPaneBox(el), force)
    }

    measure(true)
    // Second pass after flex/layout settles (pagination footer mounted, etc.).
    const t = window.setTimeout(() => measure(true), 50)

    const ro = new ResizeObserver(() => measure(false))
    ro.observe(root)

    return () => {
      ro.disconnect()
      window.clearTimeout(t)
    }
  }, [containerRef])

  // Re-measure when sort mode switches between customer groups and flat rows.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const box = readPaneBox(el)
    if (box.h < 48) return
    lastBoxRef.current = box
    const next = capacityForPane({
      paneHeight: box.h,
      headerHeight: readHeaderHeight(el, rowPxRef.current),
      rowHeight: rowPxRef.current,
      customerGrouped,
    })
    setPageSize((current) => (current === next ? current : next))
  }, [customerGrouped, containerRef])

  return pageSize
}
